import React from "react";
import {
  Box,
  Typography,
  Grid,
  IconButton,
  Link as MuiLink,
  Stack,
  Divider,
  Container,
} from "@mui/material";
import { styled } from "@mui/system";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import LanguageIcon from "@mui/icons-material/Language";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import VerifiedIcon from "@mui/icons-material/Verified";
import { useNavigate } from "react-router-dom";
import { scroller } from "react-scroll";

const COLORS = {
  orange: "#d37d36",
  orangeDark: "#b8662b",
  darkBlue: "#102840",
  white: "#ffffff",
  mutedWhite: "rgba(255,255,255,0.72)",
  softWhite: "rgba(255,255,255,0.08)",
  borderWhite: "rgba(255,255,255,0.12)",
};

const FooterContainer = styled(Container)(({ theme }) => ({
  width: "min(100%, 1240px)",
  maxWidth: "1240px !important",
  marginLeft: "auto",
  marginRight: "auto",
  paddingLeft: "clamp(18px, 4vw, 40px)",
  paddingRight: "clamp(18px, 4vw, 40px)",
  boxSizing: "border-box",
  [theme.breakpoints.up("lg")]: {
    paddingLeft: "32px",
    paddingRight: "32px",
  },
}));

const FooterTitle = ({ children }) => (
  <Typography
    sx={{
      color: COLORS.white,
      fontSize: "0.95rem",
      fontWeight: 900,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      mb: 2.2,
    }}
  >
    {children}
  </Typography>
);

const FooterLink = ({ children, onClick }) => (
  <MuiLink
    component="button"
    onClick={onClick}
    sx={{
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1.2,
      textAlign: "left",
      border: 0,
      p: 0,
      background: "transparent",
      color: COLORS.mutedWhite,
      textDecoration: "none",
      fontSize: "0.95rem",
      fontWeight: 650,
      lineHeight: 1.5,
      cursor: "pointer",
      transition: "all .22s ease",
      "&:hover": {
        color: COLORS.orange,
        transform: "translateX(4px)",
      },
      "& svg": {
        fontSize: "0.76rem",
        opacity: 0.45,
      },
      "&:hover svg": {
        opacity: 1,
      },
    }}
  >
    <span>{children}</span>
    <ArrowForwardIosIcon />
  </MuiLink>
);

const ContactRow = ({ icon, title, value }) => (
  <Stack
    direction="row"
    spacing={1.5}
    alignItems="flex-start"
    sx={{
      p: 1.6,
      borderRadius: "18px",
      bgcolor: COLORS.softWhite,
      border: `1px solid ${COLORS.borderWhite}`,
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: "14px",
        display: "grid",
        placeItems: "center",
        color: COLORS.orange,
        bgcolor: "rgba(211,125,54,0.12)",
        flexShrink: 0,
        "& svg": {
          fontSize: 21,
        },
      }}
    >
      {icon}
    </Box>

    <Box>
      <Typography
        sx={{
          color: COLORS.white,
          fontWeight: 850,
          fontSize: "0.88rem",
          mb: 0.25,
        }}
      >
        {title}
      </Typography>

      <Typography
        sx={{
          color: COLORS.mutedWhite,
          fontSize: "0.9rem",
          lineHeight: 1.55,
        }}
      >
        {value}
      </Typography>
    </Box>
  </Stack>
);

const Footer = () => {
  const navigate = useNavigate();

  const handleNavigation = (to) => {
    if (to === "/politica") {
      navigate(to);
      return;
    }

    navigate("/");

    setTimeout(() => {
      scroller.scrollTo(to, {
        smooth: true,
        duration: 800,
        offset: -84,
      });
    }, 300);
  };

  const quickLinks = [
    { label: "Inicio", to: "inicio" },
    { label: "Qué es DQPa", to: "que-es" },
    { label: "Funcionalidades", to: "funcionalidades" },
    { label: "Beneficios", to: "beneficios-clave" },
    { label: "Planes", to: "planes" },
    { label: "Contacto", to: "contacto" },
  ];

  return (
    <Box
      component="footer"
      sx={{
        position: "relative",
        overflow: "hidden",
        bgcolor: COLORS.darkBlue,
        color: COLORS.white,
        pt: { xs: 7, md: 9 },
        pb: { xs: 3, md: 4 },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: -160,
          right: -130,
          width: 360,
          height: 360,
          borderRadius: "50%",
          bgcolor: "rgba(211,125,54,0.16)",
          filter: "blur(80px)",
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          bottom: -180,
          left: -150,
          width: 420,
          height: 420,
          borderRadius: "50%",
          bgcolor: "rgba(30,75,129,0.28)",
          filter: "blur(90px)",
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)",
          backgroundSize: "54px 54px",
          opacity: 0.45,
          pointerEvents: "none",
        }}
      />

      <FooterContainer sx={{ position: "relative", zIndex: 1 }}>
        <Grid container spacing={{ xs: 4.5, md: 6 }} alignItems="flex-start">
          <Grid item xs={12} md={4.4}>
            <Stack spacing={2.2}>
              <Stack direction="row" spacing={1.4} alignItems="center">
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "18px",
                    bgcolor: COLORS.white,
                    border: `1px solid ${COLORS.borderWhite}`,
                    display: "grid",
                    placeItems: "center",
                    boxShadow: "0 20px 44px rgba(0,0,0,0.18)",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    component="img"
                    src="/images/logo_simbolo.png"
                    alt="DQPa Logo"
                    sx={{
                      width: 42,
                      height: 42,
                      objectFit: "contain",
                    }}
                  />
                </Box>

                <Box>
                  <Typography
                    sx={{
                      color: COLORS.white,
                      fontSize: "1.55rem",
                      fontWeight: 950,
                      lineHeight: 1,
                      letterSpacing: "-0.04em",
                    }}
                  >
                    DQPa
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.45,
                      color: COLORS.orange,
                      fontSize: "0.72rem",
                      fontWeight: 900,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                    }}
                  >
                    Data Quality Process Assessment
                  </Typography>
                </Box>
              </Stack>

              <Typography
                sx={{
                  color: COLORS.mutedWhite,
                  lineHeight: 1.8,
                  fontSize: "0.98rem",
                  maxWidth: 420,
                }}
              >
                Plataforma para evaluar la capacidad y madurez de los procesos
                de gobierno, gestión y calidad del dato, alineada con normas UNE
                y orientada a la mejora continua.
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  width: "fit-content",
                  px: 1.4,
                  py: 0.9,
                  borderRadius: "999px",
                  bgcolor: "rgba(255,255,255,0.07)",
                  border: `1px solid ${COLORS.borderWhite}`,
                }}
              >
                <VerifiedIcon sx={{ color: COLORS.orange, fontSize: 18 }} />
                <Typography
                  sx={{
                    color: COLORS.white,
                    fontSize: "0.78rem",
                    fontWeight: 850,
                  }}
                >
                  UNE 0077 · 0078 · 0079 · 0080
                </Typography>
              </Stack>
            </Stack>
          </Grid>

          <Grid item xs={12} sm={6} md={3.2}>
            <FooterTitle>Enlaces rápidos</FooterTitle>

            <Stack spacing={1.3}>
              {quickLinks.map((link) => (
                <FooterLink
                  key={link.to}
                  onClick={() => handleNavigation(link.to)}
                >
                  {link.label}
                </FooterLink>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12} sm={6} md={4.4}>
            <FooterTitle>Contacto</FooterTitle>

            <Stack spacing={1.4}>
              <ContactRow
                icon={<LocationOnIcon />}
                title="Ubicación"
                value={
                  <>
                    Cam. Moledores, 13005
                    <br />
                    Ciudad Real, España
                  </>
                }
              />

              <ContactRow
                icon={<PhoneIcon />}
                title="Teléfono"
                value="659 18 37 45"
              />

              <ContactRow
                icon={<EmailIcon />}
                title="Email"
                value="dqpa@dqteam.es"
              />
            </Stack>
          </Grid>
        </Grid>

        <Divider
          sx={{
            borderColor: COLORS.borderWhite,
            my: { xs: 4, md: 5 },
          }}
        />

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 2.5, md: 3 }}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack
            direction="row"
            spacing={1.2}
            alignItems="center"
            justifyContent="center"
          >
            <IconButton
              component="a"
              href="https://www.linkedin.com/company/dqteam-sl/posts/?feedView=all"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn de DQTeam"
              sx={{
                width: 46,
                height: 46,
                color: COLORS.white,
                bgcolor: "rgba(255,255,255,0.08)",
                border: `1px solid ${COLORS.borderWhite}`,
                transition: "all .25s ease",
                "&:hover": {
                  color: COLORS.white,
                  bgcolor: COLORS.orange,
                  transform: "translateY(-3px)",
                  boxShadow: "0 16px 34px rgba(211,125,54,0.26)",
                },
              }}
            >
              <LinkedInIcon />
            </IconButton>

            <IconButton
              component="a"
              href="https://dqteam.es"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Página web de DQTeam"
              sx={{
                width: 46,
                height: 46,
                color: COLORS.white,
                bgcolor: "rgba(255,255,255,0.08)",
                border: `1px solid ${COLORS.borderWhite}`,
                transition: "all .25s ease",
                "&:hover": {
                  color: COLORS.white,
                  bgcolor: COLORS.orange,
                  transform: "translateY(-3px)",
                  boxShadow: "0 16px 34px rgba(211,125,54,0.26)",
                },
              }}
            >
              <LanguageIcon />
            </IconButton>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 1.2, sm: 2.5 }}
            alignItems="center"
            justifyContent="center"
            sx={{ textAlign: "center" }}
          >
            <Typography
              sx={{
                color: "rgba(255,255,255,0.62)",
                fontSize: "0.88rem",
                lineHeight: 1.5,
              }}
            >
              © {new Date().getFullYear()} DQPa. Todos los derechos reservados.
            </Typography>

            <MuiLink
              component="button"
              onClick={() => handleNavigation("/politica")}
              sx={{
                border: 0,
                background: "transparent",
                p: 0,
                color: COLORS.mutedWhite,
                textDecoration: "none",
                fontSize: "0.88rem",
                fontWeight: 750,
                cursor: "pointer",
                transition: "color .22s ease",
                "&:hover": {
                  color: COLORS.orange,
                },
              }}
            >
              Política de privacidad
            </MuiLink>
          </Stack>
        </Stack>
      </FooterContainer>
    </Box>
  );
};

export default Footer;